import { inject } from '@angular/core';
import { LoadingController, NavController } from '@ionic/angular/standalone';
import { TranslocoService } from '@ngneat/transloco';
import {
  AuthService,
  AuthStore,
  SettingsStore,
  UserSettingsStore,
  UserAppSettingsStore,
  ListColumnPreferencesStore,
  ListSortPreferencesStore,
  DropdownPinPreferencesStore,
  SummaryStore,
  WebsocketService,
  AppSurfaceStore,
} from '@marineform/manage-data-access';
import { from, switchMap, tap, catchError, EMPTY, firstValueFrom } from 'rxjs';

/**
 * Starts manage from the sealed session. An organisation loads the operator;
 * otherwise the renter. The active language is loaded before the first paint
 * so the first screen is not blank.
 */
export function manageAuthInitializer() {
  const authService = inject(AuthService);
  const authStore = inject(AuthStore);
  const appSurfaceStore = inject(AppSurfaceStore);
  const settingsStore = inject(SettingsStore);
  const userSettingsStore = inject(UserSettingsStore);
  const userAppSettingsStore = inject(UserAppSettingsStore);
  const listColumnPreferencesStore = inject(ListColumnPreferencesStore);
  const listSortPreferencesStore = inject(ListSortPreferencesStore);
  const dropdownPinPreferencesStore = inject(DropdownPinPreferencesStore);
  const summaryStore = inject(SummaryStore);
  const transloco = inject(TranslocoService);
  const ws = inject(WebsocketService);
  const navCtrl = inject(NavController);
  const loadingController = inject(LoadingController);

  const finishUnauthenticated = () =>
    transloco.load(transloco.getActiveLang()).pipe(switchMap(() => EMPTY));

  const bootstrapOperator = () =>
    authStore.login().pipe(
      tap(() => {
        ws.init();
        summaryStore.loadCounts().subscribe();
      }),
      switchMap(() =>
        settingsStore.getAll().pipe(
          switchMap(() => userSettingsStore.load()),
          switchMap(() => userAppSettingsStore.load()),
          tap(() => {
            const settingsValues =
              userAppSettingsStore.userAppSettings?.()?.settingsValues;
            listColumnPreferencesStore.hydrateFromBag(settingsValues);
            listSortPreferencesStore.hydrateFromBag(settingsValues);
            dropdownPinPreferencesStore.hydrateFromBag(settingsValues);
          }),
        ),
      ),
      switchMap(() => transloco.load(transloco.getActiveLang())),
    );

  const bootstrapRenter = () =>
    authStore
      .loadRenterUser()
      .pipe(switchMap(() => transloco.load(transloco.getActiveLang())));

  const bootstrapFromSession = () =>
    authService.getSessionUser().pipe(
      switchMap((sessionUser) =>
        sessionUser.organizationId == null
          ? bootstrapRenter()
          : bootstrapOperator(),
      ),
      catchError(() => finishUnauthenticated()),
    );

  const nativeAuthLoadingInKey = 'manage|login|logging-in';

  let nativeAuthLoadingOverlay: Awaited<
    ReturnType<LoadingController['create']>
  > | null = null;
  let nativeAuthLoadingMessageKey: string | null = null;

  const ensureNativeAuthLoadingOverlay = async (
    messageKey = nativeAuthLoadingInKey,
  ) => {
    if (
      nativeAuthLoadingOverlay &&
      nativeAuthLoadingMessageKey !== messageKey
    ) {
      await nativeAuthLoadingOverlay.dismiss();
      nativeAuthLoadingOverlay = null;
      nativeAuthLoadingMessageKey = null;
    }

    if (nativeAuthLoadingOverlay) {
      return nativeAuthLoadingOverlay;
    }

    nativeAuthLoadingMessageKey = messageKey;
    nativeAuthLoadingOverlay = await loadingController.create({
      message: transloco.translate(messageKey),
      backdropDismiss: false,
      cssClass: 'mg-action-loading',
      spinner: 'dots',
    });
    return nativeAuthLoadingOverlay;
  };

  const nativeAuthLoading = {
    prepare: async () => {
      await ensureNativeAuthLoadingOverlay(nativeAuthLoadingInKey);
    },
    show: async (messageKey = nativeAuthLoadingInKey) => {
      const overlay = await ensureNativeAuthLoadingOverlay(messageKey);
      await overlay.present();
    },
    dismiss: async () => {
      await nativeAuthLoadingOverlay?.dismiss();
      nativeAuthLoadingOverlay = null;
      nativeAuthLoadingMessageKey = null;
    },
  };

  authService.onNativeAuthLoading(nativeAuthLoading);

  authService.onNativeAuthExchanged(async () => {
    try {
      await firstValueFrom(
        bootstrapFromSession().pipe(
          switchMap(() =>
            from(navCtrl.navigateRoot(appSurfaceStore.homePath())),
          ),
        ),
      );
    } finally {
      await nativeAuthLoading.dismiss();
    }
  });

  return from(authService.initNativeAuth()).pipe(
    catchError(() => finishUnauthenticated()),
    switchMap(() => bootstrapFromSession()),
  );
}
